import { Link } from "react-router-dom";
import { ArrowUpRight, ExternalLink, FileText, MapPin, Mail, Phone, Receipt, ShieldCheck } from "lucide-react";

import Breadcrumb from "@/components/Breadcrumb";
import Reveal from "@/components/motion/Reveal";
import BrandGraphic from "@/components/motion/BrandGraphic";
import PageSideAura from "@/components/motion/PageSideAura";
import { SITE } from "@/lib/constants";
import useDocumentHead from "@/lib/useDocumentHead";
import SEO, { PAGE_SEO } from "@/components/SEO";
import JsonLd, { breadcrumbSchema } from "@/components/JsonLd";

/**
 * 特定商取引法に基づく表示 / Commercial Transactions Law disclosure.
 *
 * This page exists to satisfy Japan's Act on Specified Commercial Transactions
 * (特定商取引法) for the corporate operating company. Per the changelog content
 * rules, this page does NOT discuss internal tooling, hosting, or anything
 * beyond statutory disclosure plus the corporate operating facts.
 */
export default function CommercialTransactionsPage({ lang }) {
  const isJa = lang === "ja";
  // Title + meta — owned by <SEO /> via Helmet (see top of returned JSX).

  const rows = isJa ? ROWS_JA : ROWS_EN;

  return (
    <article data-testid="commercial-transactions-page" className="bg-white">
      <SEO {...PAGE_SEO.commercialTransactions[lang]} path={`/${lang}/legal/commercial-transactions`} lang={lang} />
      <JsonLd
        data={breadcrumbSchema([
          { name: isJa ? "ホーム" : "Home", url: `/${lang}` },
          { name: isJa ? "法務情報" : "Legal", url: `/${lang}/legal/commercial-transactions` },
          { name: isJa ? "特定商取引法に基づく表記" : "Commercial Transactions", url: `/${lang}/legal/commercial-transactions` },
        ])}
      />
      <Breadcrumb
        lang={lang}
        crumbs={[
          { label: isJa ? "法務情報" : "Legal", path: "/legal" },
          { label: isJa ? "特定商取引法に基づく表示" : "Commercial Transactions" },
        ]}
      />

      {/* Hero */}
      <section className="relative bg-white overflow-hidden">
        <BrandGraphic
          variant="grid"
          color="#0F1D2D"
          className="absolute inset-0 w-full h-full opacity-15 pointer-events-none"
        />
        <div className="relative max-w-5xl mx-auto px-5 sm:px-6 md:px-10 pt-2 md:pt-4 pb-8">
          <Reveal as="div" variant="up" className="inline-flex items-center gap-2 mb-3">
            <FileText className="h-4 w-4 text-[#5B2E83]" strokeWidth={1.75} />
            <span className="text-[10px] tracking-[0.28em] font-mono-display text-[#5B2E83] font-bold uppercase">
              {isJa ? "法務情報 / Legal" : "Legal Notice"}
            </span>
          </Reveal>
          <Reveal
            as="h1"
            variant="up"
            delay={100}
            className={[
              isJa ? "font-ja" : "font-display",
              "text-[#0F1D2D] text-4xl md:text-5xl lg:text-[54px] tracking-tighter font-bold leading-[1.05]",
            ].join(" ")}
          >
            {isJa ? "特定商取引法に基づく表示" : "Commercial Transactions"}
          </Reveal>
          <Reveal
            as="p"
            variant="up"
            delay={170}
            className="mt-4 max-w-2xl text-[15px] md:text-[16px] leading-[1.7] text-[#475569]"
          >
            {isJa
              ? "本表示は、特定商取引法第11条および関連法令の要求に基づき、当社の事業者情報を公開するものです。"
              : "This disclosure is published under Article 11 of Japan's Act on Specified Commercial Transactions (特定商取引法) and related regulations, providing the required operating-company information for the public."}
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          MIDDLE CONTENT — wrapped together so a single subtle side aura
          sits behind ONLY the disclosure table, not the hero above or
          the footer note below.
          ═══════════════════════════════════════════════════════════════ */}
      <div className="relative isolate" data-testid="commercial-transactions-middle">
        <PageSideAura accent="#0E5FB0" accent2="#5B2E83" testId="commercial-transactions-side-aura" />

      {/* Disclosure rows */}
      <section className="bg-white relative">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 md:px-10 pb-16">
          <dl
            data-testid="commercial-transactions-list"
            className="divide-y divide-[#E2E8F0] border-y border-[#E2E8F0]"
          >
            {rows.map((row, i) => {
              const Icon = row.icon || null;
              return (
                <Reveal
                  as="div"
                  variant="up"
                  delay={i * 50}
                  key={row.key}
                  data-testid={`commercial-transactions-${row.key}`}
                  className="grid grid-cols-12 gap-4 py-6 md:py-7"
                >
                  <dt className="col-span-12 md:col-span-4">
                    <div className="flex items-center gap-2">
                      {Icon && <Icon className="h-4 w-4 text-[#5B2E83]" strokeWidth={1.6} />}
                      <span className="font-mono-display text-[11px] tracking-[0.22em] text-[#5B2E83] uppercase font-bold">
                        {row.key.toUpperCase().replace(/-/g, " ")}
                      </span>
                    </div>
                    <div
                      className={[
                        "mt-1",
                        isJa ? "font-ja" : "font-display",
                        "text-[#0F1D2D] text-[16px] md:text-[17px] font-bold tracking-tight",
                      ].join(" ")}
                    >
                      {row.label}
                    </div>
                  </dt>
                  <dd className="col-span-12 md:col-span-8 text-[14.5px] leading-[1.85] text-[#475569] whitespace-pre-line">
                    {row.linkHref ? (
                      <a
                        href={row.linkHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-[#0E5FB0] hover:underline font-semibold"
                        data-testid={`commercial-transactions-${row.key}-link`}
                      >
                        {row.value}
                        <ExternalLink className="h-3 w-3 opacity-70" strokeWidth={2.25} aria-hidden />
                      </a>
                    ) : (
                      row.value
                    )}
                    {row.note && (
                      <div className="mt-1 text-[12.5px] text-[#64748B]">{row.note}</div>
                    )}
                  </dd>
                </Reveal>
              );
            })}
          </dl>
        </div>
      </section>
      </div>{/* ═════════ END middle content wrapper (aura ends here) ═════════ */}

      {/* Footer note */}
      <section className="bg-[#F8F9FA] border-t border-[#E2E8F0]">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 md:px-10 py-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="text-[13px] leading-relaxed text-[#475569] max-w-2xl">
            {isJa
              ? "本表示の内容についてご不明な点がございましたら、お問い合わせフォームよりご連絡ください。"
              : "If you have any questions about this disclosure, please reach us through the contact form."}
          </p>
          <Link
            to={`/${lang}/contact`}
            data-testid="commercial-transactions-contact"
            className="inline-flex items-center gap-2 min-h-[44px] px-5 rounded-full bg-[#0E5FB0] text-white text-[12.5px] font-semibold tracking-tight hover:bg-[#084785] transition-colors"
          >
            {isJa ? "お問い合わせ" : "Contact us"}
            <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} />
          </Link>
        </div>
      </section>
    </article>
  );
}

/* ---------- Statutory rows ---------- */

const ROWS_EN = [
  {
    key: "business-name",
    label: "Business Name",
    value: `${SITE.legalNameEn}\n${SITE.legalNameJaRegistered}`,
    note: "Registered legal name on the National Tax Agency invoice registry.",
    icon: FileText,
  },
  {
    key: "representative",
    label: "Representative",
    value: `${SITE.representativeEn}\n${SITE.representativeJa}`,
  },
  {
    key: "address",
    label: "Registered Address",
    value: `${SITE.addressFullEn}\n${SITE.addressFullJa}`,
    icon: MapPin,
  },
  {
    key: "phone",
    label: "Phone",
    value: `${SITE.phone}\nReception: Monday – Friday, 9:00 – 17:00 JST`,
    icon: Phone,
  },
  {
    key: "email",
    label: "Email",
    value: SITE.email,
    icon: Mail,
  },
  {
    key: "business-hours",
    label: "Business Hours",
    value:
      "Monday – Friday: 9:00 – 17:00 JST\nClosed: Saturdays, Sundays and Japanese public holidays",
  },
  {
    key: "corporate-number",
    label: "Corporate Number (法人番号)",
    value: SITE.corporateNumber,
    linkHref: SITE.corporateNumberRegistryUrl,
    note: "Verifiable via National Tax Agency public registry.",
    icon: ShieldCheck,
  },
  {
    key: "invoice-registration",
    label: "Invoice Registration Number",
    value: SITE.invoiceRegistration,
    linkHref: SITE.invoiceRegistrationRegistryUrl,
    note: `適格請求書発行事業者登録番号 · Registered ${SITE.invoiceRegistrationDateEn}. Verifiable on the NTA invoice registry.`,
    icon: Receipt,
  },
  {
    key: "founded",
    label: "Founded",
    value: SITE.establishedDisplayEn,
  },
  {
    key: "capital",
    label: "Capital",
    value: SITE.capitalDisplayEn,
  },
  {
    key: "services",
    label: "Scope of Business",
    value:
      "Operation of independent digital services in Japan across mobility, records and bilingual customer support. Each operated service publishes its own complete Commercial Transactions disclosure with service-level pricing, payment terms, delivery timing and refund policy.",
  },
  {
    key: "language-support",
    label: "Customer Support Languages",
    value: "Japanese and English",
  },
  {
    key: "service-disclosures",
    label: "Service-Level Disclosures",
    value:
      "For service-specific commercial-transactions disclosures (pricing, payment methods, delivery, refund and cancellation terms), please refer to the official site of each operated service:\n• japanetccard.com\n• japantollreceipts.jp\n• japanchangemoney.com",
    icon: ShieldCheck,
  },
];

const ROWS_JA = [
  {
    key: "business-name",
    label: "販売業者",
    value: `${SITE.legalNameJa}\n${SITE.legalNameEn}`,
    note: "国税庁 適格請求書発行事業者公表サイトの登録名称と一致します。",
    icon: FileText,
  },
  {
    key: "representative",
    label: "代表責任者",
    value: `${SITE.representativeJa}\n(${SITE.representativeEn})`,
  },
  {
    key: "address",
    label: "所在地",
    value: `${SITE.addressFullJa}\n${SITE.addressFullEn}`,
    icon: MapPin,
  },
  {
    key: "phone",
    label: "電話番号",
    value: `${SITE.phoneDisplay}\n受付時間: 平日 9:00 - 17:00 (日本時間)`,
    icon: Phone,
  },
  {
    key: "email",
    label: "メールアドレス",
    value: SITE.email,
    icon: Mail,
  },
  {
    key: "business-hours",
    label: "営業時間",
    value: "平日 9:00 - 17:00 (日本時間)\n定休日: 土曜・日曜・日本の祝日",
  },
  {
    key: "corporate-number",
    label: "法人番号",
    value: SITE.corporateNumber,
    linkHref: SITE.corporateNumberRegistryUrl,
    note: "国税庁 法人番号公表サイトにて検索・照会できます。",
    icon: ShieldCheck,
  },
  {
    key: "invoice-registration",
    label: "適格請求書発行事業者登録番号",
    value: SITE.invoiceRegistration,
    linkHref: SITE.invoiceRegistrationRegistryUrl,
    note: `${SITE.invoiceRegistrationDateJa}登録。国税庁 適格請求書発行事業者公表サイトにて検索・照会できます。`,
    icon: Receipt,
  },
  {
    key: "founded",
    label: "設立年月日",
    value: SITE.establishedDisplayJa,
  },
  {
    key: "capital",
    label: "資本金",
    value: SITE.capitalDisplayJa,
  },
  {
    key: "services",
    label: "事業内容",
    value:
      "日本国内における、移動・記録・バイリンガル顧客対応に関する独立系デジタルサービスの運営。各運営サービスは、料金・支払方法・引渡時期・返品キャンセル規定を含む完全な特定商取引法表示を、サービス公式サイトに掲載しています。",
  },
  {
    key: "language-support",
    label: "対応言語",
    value: "日本語・英語",
  },
  {
    key: "service-disclosures",
    label: "サービス別の特定商取引法表示",
    value:
      "各サービスの料金・支払方法・引渡時期・返品キャンセル等の詳細は、各サービス公式サイトの特定商取引法表示をご参照ください。\n• japanetccard.com\n• japantollreceipts.jp\n• japanchangemoney.com",
    icon: ShieldCheck,
  },
];
